import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface AnimatedBackgroundProps {
  className?: string;
  variant?: "dots" | "grid" | "gradient" | "mesh";
}

export function AnimatedBackground({ 
  className,
  variant = "gradient" 
}: AnimatedBackgroundProps) {
  if (variant === "dots") {
    return (
      <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle, hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: "24px 24px",
          }}
        />
      </div>
    );
  }

  if (variant === "grid") {
    return (
      <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />
      </div>
    );
  }

  if (variant === "mesh") {
    return (
      <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
        <motion.div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(at 40% 20%, hsl(var(--primary) / 0.05) 0px, transparent 50%),
              radial-gradient(at 80% 0%, hsl(var(--primary) / 0.03) 0px, transparent 50%),
              radial-gradient(at 0% 50%, hsl(var(--primary) / 0.05) 0px, transparent 50%),
              radial-gradient(at 80% 50%, hsl(var(--primary) / 0.03) 0px, transparent 50%),
              radial-gradient(at 0% 100%, hsl(var(--primary) / 0.04) 0px, transparent 50%),
              radial-gradient(at 80% 100%, hsl(var(--primary) / 0.02) 0px, transparent 50%)
            `,
          }}
          animate={{
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>
    );
  }

  // Default gradient
  return (
    <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5"
        animate={{
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
}

interface GradientBlobProps {
  className?: string;
}

export function GradientBlob({ className }: GradientBlobProps) {
  return (
    <motion.div
      className={cn(
        "absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-30",
        "bg-gradient-to-r from-primary/20 to-primary/5",
        className
      )}
      animate={{
        scale: [1, 1.2, 1],
        rotate: [0, 180, 360],
        borderRadius: ["60% 40% 30% 70% / 60% 30% 70% 40%", "30% 60% 70% 40% / 50% 60% 30% 60%", "60% 40% 30% 70% / 60% 30% 70% 40%"],
      }}
      transition={{
        duration: 20,
        repeat: Infinity,
        ease: "linear",
      }}
    />
  );
}
